"use client";
/**
 * SANATAN DECODE — v16 "SPLIT / तब · अब". 21s @ 30fps, 1280x720.
 *
 * Every episode on this channel is the same sentence: *you still do this — here is where it
 * came from.* v16 makes that the LAYOUT rather than something the narration has to carry.
 *
 * The frame is cut in two live places. LEFT is तब — the gurukul panorama, walking left to
 * right. RIGHT is अब — a present-day Indian street panorama (generated for this version,
 * same treatment, and reusable as the "now" ground for every future episode), walking the
 * opposite way so the seam reads as two places, not one image with a line on it.
 *
 * The divider is the argument. It slides beat to beat to say which side is being spoken
 * about: 88% तब while the rishi is named, back to 50% while the bloodline is drawn across
 * it, then 34% — mostly अब — for the शास्त्र beat, because the rule is a thing people are
 * still obeying this week. The marigold thread is the only object allowed to cross it.
 *
 * ⚠️ Two grounds means the walk can no longer hide a weak beat: whatever sits on the seam is
 * the shot. Place one object there per beat and nothing else near it.
 */
import React from "react";
import { AbsoluteFill, Audio, staticFile, interpolate, Easing } from "remotion";
import {
  INK, GOLD, MARIGOLD, KUMKUM, MUTED_C, CREAM, deva, inter,
  f, useStep, useKeys, useSpringAt, W, WALK,
  Pano, Grain, Petals, Word, Row, Line, Beat, useSwing, Prop, Evidence, Sticker,
  PersistentThread, Furniture,
} from "./sanatan/kit";

/** अब walks the other way, so the two halves never drift in sympathy. */
const WALK_REV: [number, number][] = WALK.map(([t, v]) => [t, 1 - v] as [number, number]);

/** where the seam sits, as a fraction of the frame. This is the film's argument. */
const SEAM: [number, number][] = [
  [0.0, 0.60], [3.55, 0.60],
  [4.05, 0.88],                 // ऋषि — तब fills the frame
  [5.55, 0.88],
  [6.10, 0.50],                 // रक्त-रेखा — dead even, the thread crosses
  [10.35, 0.50],
  [10.95, 0.34],                // शास्त्र — mostly अब: the rule is still being obeyed
  [15.55, 0.34],
  [16.10, 0.52],                // विज्ञान — even again
  [21.2, 0.52],
];

const useSeam = () => {
  const frame = useStep();
  return interpolate(frame, SEAM.map((k) => f(k[0])), SEAM.map((k) => k[1]), {
    extrapolateLeft: "clamp", extrapolateRight: "clamp", easing: Easing.inOut(Easing.cubic),
  });
};

const Ground: React.FC = () => {
  const p = useSeam();
  const q = (1 - p) * 100;
  return (
    <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
      {/* तब */}
      <AbsoluteFill style={{ clipPath: `inset(0 ${q.toFixed(2)}% 0 0)` }}>
        <Pano src="sanatan/pano/gurukul_pano.png" blur={4.0} sat={0.60} bright={1.12} contrast={0.84} wash={0.30} />
      </AbsoluteFill>
      {/* अब — cooler and a touch flatter, so the eye reads two different days */}
      <AbsoluteFill style={{ clipPath: `inset(0 0 0 ${(p * 100).toFixed(2)}%)` }}>
        <Pano src="sanatan/pano/modern_pano.png" blur={4.4} sat={0.50} bright={1.14} contrast={0.80} wash={0.34} walk={WALK_REV} />
      </AbsoluteFill>
      {/* the seam itself — a torn cream edge, not a ruled line */}
      <div style={{
        position: "absolute", top: -20, bottom: -20, left: `calc(${(p * 100).toFixed(2)}% - 7px)`, width: 14,
        background: "linear-gradient(90deg, rgba(255,250,236,0) 0%, #FFFAEC 22%, #FFFAEC 78%, rgba(255,250,236,0) 100%)",
        boxShadow: "0 0 26px rgba(72,46,16,0.28)",
      }} />
    </AbsoluteFill>
  );
};

/** तब / अब chips, parked against the seam on either side so they move with it. */
const SideTags: React.FC = () => {
  const p = useSeam();
  const s = useSpringAt(0.35, 11, 130, 0.9);
  const chip = (text: string, right: boolean): React.CSSProperties => ({
    position: "absolute", top: 96,
    left: right ? `calc(${(p * 100).toFixed(2)}% + 22px)` : undefined,
    right: right ? undefined : `calc(${((1 - p) * 100).toFixed(2)}% + 22px)`,
    fontFamily: deva, fontSize: 19, fontWeight: 800, wordSpacing: 4,
    color: CREAM, background: right ? INK : "#7A4A1E", padding: "4px 18px", borderRadius: 2,
    opacity: Math.min(1, s * 1.6),
    transform: `translateY(${(1 - s) * 16}px)`,
  });
  return (
    <>
      <div style={chip("तब", false)}>तब</div>
      <div style={chip("अब", true)}>अब</div>
    </>
  );
};

/* ══ PERSISTENT — the sage stands in तब for the whole first half ══ */
const PersistentSage: React.FC = () => {
  const frame = useStep();
  const sIn = useSpringAt(3.9, 9, 120);
  const sOut = useSpringAt(10.2, 200, 70, 0.5);
  const left = useKeys([[3.9, 420], [5.6, 420], [6.3, 108], [10.2, 108]]);
  const top = useKeys([[3.9, 176], [5.6, 176], [6.3, 246], [10.2, 246]]);
  const h = useKeys([[3.9, 372], [5.6, 372], [6.3, 296], [10.2, 296]]);
  if (frame < f(3.7) || frame > f(10.9)) return null;
  const pend = Math.sin((frame - f(3.9)) / 40) * 0.9 * sIn;
  return (
    <div style={{
      position: "absolute", left, top,
      transform: `translate(${-140 * (1 - sIn)}px, ${-240 * (1 - sIn) + 200 * sOut}px) rotate(${8 * (1 - sIn) + pend - 22 * sOut}deg)`,
      transformOrigin: "50% -20%",
      opacity: Math.min(1, sIn * 2.2) * (1 - Math.min(1, sOut * 1.4)),
    }}>
      <Sticker src="sanatan/photo/ph_sage.png" h={h} />
    </div>
  );
};

/* ══ BEATS ═════════════════════════════════════════════════════ */
/** The anchor sits ON the seam: one object that belongs to both sides at once. */
const BeatA: React.FC = () => {
  const p = useSeam();
  return (
    <>
      <div style={{ position: "absolute", left: `calc(${(p * 100).toFixed(2)}% - 300px)`, top: 400 }}>
        <Evidence src="sanatan/photo/ph_manuscript.png" w={600} left={0} top={0}
          inAt={-0.4} outAt={3.55} from="right" tilt={-2} label="ताड़पत्र" />
      </div>
      <div style={{ position: "absolute", left: 0, right: 0, top: 152 }}>
        <Row gap={14}>{[0, 1, 2, 3, 4, 5, 6].map((i) => <Word key={i} idx={i} size={36} color={MUTED_C} weight={600} underline={i === 2} outAt={3.6} />)}</Row>
        <div style={{ height: 14 }} />
        <Row>
          <Word idx={8} size={72} weight={900} outAt={3.62} />
          <Word idx={9} size={72} weight={900} outAt={3.64} />
          <Word idx={10} size={72} weight={900} color={KUMKUM} strike outAt={3.66} />
          <Word idx={11} size={72} weight={900} outAt={3.68} />
          <Word idx={12} size={72} weight={900} outAt={3.7} />
        </Row>
      </div>
    </>
  );
};

const BeatB: React.FC = () => {
  const halo = useSpringAt(4.35, 11, 80);
  const gone = useSpringAt(5.5, 200, 70);
  return (
    <>
      <svg width={1280} height={720} style={{ position: "absolute", opacity: halo * 0.5 * (1 - gone) }}>
        <circle cx={562} cy={266} r={132 + halo * 34} fill="none" stroke={GOLD} strokeWidth={2} />
        <circle cx={562} cy={266} r={98 + halo * 22} fill="none" stroke={MARIGOLD} strokeWidth={1.5} />
      </svg>
      <div style={{ position: "absolute", left: 0, right: 0, top: 556, textAlign: "center" }}>
        <Row><Word idx={15} size={76} weight={900} color={KUMKUM} underline outAt={5.52} /></Row>
      </div>
    </>
  );
};

/** The bloodline is the one thing allowed to cross the seam — drawn from the sage in तब to
    the man in अब, so the argument is made by geometry rather than by the caption. */
const BeatC: React.FC = () => (
  <>
    <div style={{ position: "absolute", left: 900, top: 236 }}>
      <Evidence src="sanatan/photo/ph_modern_man.png" h={330} left={0} top={0}
        inAt={7.0} outAt={10.25} from="right" tilt={1.4} />
    </div>
    <div style={{ position: "absolute", left: 66, top: 596 }}>
      <Line text="ऋषि" at={6.30} outAt={10.2} size={26} color={INK} weight={800} />
    </div>
    <div style={{ position: "absolute", left: 968, top: 596 }}>
      <Line text="आप" at={7.52} outAt={10.2} size={26} color={KUMKUM} weight={800} />
    </div>
    <div style={{ position: "absolute", left: 316, top: 300, display: "flex", gap: 22 }}>
      {([["पुत्र", 6.80], ["पौत्र", 6.98], ["प्रपौत्र", 7.16], ["कई पीढ़ी", 7.36]] as [string, number][]).map(([t, at]) => (
        <Line key={t} text={t} at={at} outAt={10.2} size={21} color={MUTED_C} weight={700} />
      ))}
    </div>
    <div style={{ position: "absolute", left: 0, right: 0, top: 452, textAlign: "center" }}>
      <Row gap={14}>
        <Word idx={26} size={48} weight={800} outAt={10.2} />
        <Word idx={27} size={48} weight={800} outAt={10.22} />
        <Word idx={28} size={60} weight={900} color={KUMKUM} underline outAt={10.24} />
      </Row>
    </div>
  </>
);

/** अब side dominates. The couple stands in the present; the copper plate that governs them
    is left behind in तब, small, on the far side of the seam. That gap IS the episode. */
const BeatDE: React.FC = () => {
  const p = useSeam();
  return (
    <>
      <div style={{ position: "absolute", left: `calc(${(p * 100).toFixed(2)}% + 140px)`, top: 96 }}>
        <Prop src="sanatan/couple.png" w={380} left={0} top={0} inAt={10.65} outAt={15.42} from="top" amp={11} travel={280} />
      </div>
      <Evidence src="sanatan/photo/ph_copperplate.png" h={172} left={44} top={332}
        inAt={11.61} outAt={15.44} from="left" tilt={-5} label="ताम्रपत्र · शास्त्र" />
      <div style={{ position: "absolute", left: 340, right: 40, top: 470, textAlign: "center" }}>
        <Row gap={16}>
          <Word idx={38} size={66} weight={900} outAt={15.4} />
          <Word idx={39} size={66} weight={900} outAt={15.42} />
          <Word idx={40} size={74} weight={900} color={KUMKUM} outAt={15.44} />
        </Row>
      </div>
    </>
  );
};

const BeatF: React.FC = () => {
  const frame = useStep();
  const CX = 640, PY = 246, CY = 404;
  const pA = useSpringAt(15.9, 9, 140);
  const pB = useSpringAt(16.2, 9, 140);
  const kid = useSpringAt(17.15, 8, 150);
  const term = useSpringAt(17.43, 10, 120);
  const bob = Math.sin(frame / 24) * 5;
  const Allele: React.FC<{ x: number; fill: string; opacity?: number }> = ({ x, fill, opacity = 1 }) => (
    <rect x={x - 7} y={-20} width={14} height={40} rx={7} fill={fill} opacity={opacity} />
  );
  const Carrier: React.FC<{ x: number; s: number; ph: number }> = ({ x, s, ph }) => (
    <g opacity={s} transform={`translate(${x},${PY + Math.sin((frame + ph) / 26) * 6}) scale(${0.8 + s * 0.2}) rotate(${8 * (1 - s)})`}>
      <circle r={44} fill="#FDF8EC" stroke={INK} strokeWidth={4} />
      <Allele x={-15} fill={INK} opacity={0.22} />
      <Allele x={15} fill={KUMKUM} />
    </g>
  );
  return (
    <>
      {/* the science beat straddles the seam deliberately — genetics does not care which
          side of the split you were born on */}
      <svg width={1280} height={720} style={{ position: "absolute" }}>
        <Carrier x={CX - 132} s={pA} ph={0} />
        <Carrier x={CX + 132} s={pB} ph={40} />
        <g opacity={kid} transform={`translate(${CX},${CY + bob}) scale(${0.78 + kid * 0.22}) rotate(${-10 * (1 - kid)})`}>
          <circle r={46} fill={KUMKUM} opacity={0.14} />
          <circle r={46} fill="none" stroke={KUMKUM} strokeWidth={5} />
          <Allele x={-15} fill={KUMKUM} />
          <Allele x={15} fill={KUMKUM} />
        </g>
      </svg>
      <div style={{
        position: "absolute", left: 0, right: 0, top: 508, textAlign: "center",
        opacity: Math.min(1, term * 1.4),
        transform: `translateY(${(1 - term) * 34}px) rotate(${3 * (1 - term)}deg)`,
      }}>
        <div style={{ fontFamily: inter, fontSize: 44, fontWeight: 800, letterSpacing: 6, color: KUMKUM }}>CONSANGUINITY</div>
        <div style={{ height: 8 }} />
        <div style={{ fontFamily: deva, fontSize: 25, fontWeight: 700, color: INK, opacity: 0.82 }}>विज्ञान ने इसे एक नाम दिया।</div>
      </div>
    </>
  );
};

export const SanatanDecodeGotraV16: React.FC = () => (
  <AbsoluteFill style={{ backgroundColor: "#F2E6CC", overflow: "hidden" }}>
    <Audio src={staticFile("sanatan/gotra.mp3")} />
    <Ground />
    {/* ⛔ no whole-frame camera scale here — pushing in on a split frame slides the seam
        against its own contents and the two halves stop reading as places. */}
    <AbsoluteFill>
      <SideTags />
      <Beat from={0} to={3.75}><BeatA /></Beat>
      <Beat from={3.9} to={5.6}><BeatB /></Beat>
      <Beat from={5.8} to={10.4}><BeatC /></Beat>
      <Beat from={10.6} to={15.6}><BeatDE /></Beat>
      <Beat from={15.8} to={21.1}><BeatF /></Beat>
      <PersistentSage />
      <PersistentThread />
    </AbsoluteFill>
    <Petals n={10} op={0.18} />
    <Grain />
    <Furniture />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
